import React, { FC, useEffect, useState } from 'react'
import {
  Box,
  Flex,
  FlexBoxGrid,
  Switch,
  ButtonOutline,
  ButtonIcon
} from 'front-end/src/components'
import { css } from '@emotion/react'
import { t, Trans } from '@lingui/macro'
import {
  arrayMove,
  SortableContainer,
  SortableElement
} from 'react-sortable-hoc'

import GeneralRemoveModal from 'front-end/src/components/common/general-remove-modal/GeneralRemoveModal'
import IconCursorMove from 'front-end/src/components/common/icon/IconCursorMove'
import IconPencil from 'front-end/src/components/common/icon/IconPencil'
import IconPlus2 from 'front-end/src/components/common/icon/IconPlus2'
import IconTrash from 'front-end/src/components/common/icon/IconTrash'
import { FormRow } from 'front-end/src/components/common/form/Form'

import Spinner from '../../../components/common/spinner/Spinner'
import { Bookmark } from '../../workspace-page/components/bookmark/Bookmark'
import { BookmarkFormModal } from './BookmarkFormModal'
import {
  useRemoveBookmarkMutation,
  useSetBookmarksOrderMutation,
  useSetBookmarksTypeMutation,
  useWorkspaceBookmarksQuery
} from './WorkspaceBookmarksTab.codegen'
import { WorkspaceBookmarkTypeEnum } from '../../../graphql/private/PrivateSchemaTypes'

import '../workspace-members-tab/MembersList.less'
import { brandColors } from 'shared/basicPalette'

export const WorkspaceBookmarksTab: FC<{
  workspaceId: number
}> = ({ workspaceId }) => {
  const [showBookmarkFormModal, setShowBookmarkFormModal] = useState(false)
  const [bookmarkEditId, setBookmarkEditId] = useState<number | null>(null)
  const [largerBookmark, setLargerBookmark] = useState(false)
  const [removeModalBookmarkId, setRemoveModalBookmarkId] = useState<
    number | null
  >(null)

  const [removeBookmark, { loading: removeBookmarkLoading }] =
    useRemoveBookmarkMutation()

  const [setBookmarksType, { loading: setBookmarksTypeLoading }] =
    useSetBookmarksTypeMutation()

  const [setBookmarksOrder, { loading: setBookmarksOrderLoading }] =
    useSetBookmarksOrderMutation()

  const {
    data,
    loading: bookmarksLoading,
    refetch: refetchBookmarks
  } = useWorkspaceBookmarksQuery({
    variables: {
      workspaceId
    }
  })

  useEffect(() => {
    // Set the type switch to the correct state, determined from first bookmark
    setLargerBookmark(
      bookmarks && bookmarks.length > 0
        ? bookmarks[0].type === WorkspaceBookmarkTypeEnum.BIG
        : false
    )
  }, [data])

  const bookmarks = data?.workspace?.bookmarkList
  const loading =
    bookmarksLoading ||
    removeBookmarkLoading ||
    setBookmarksTypeLoading ||
    setBookmarksOrderLoading

  if (
    (!bookmarks && loading) ||
    data?.workspace?.__typename !== 'WorkspaceModelAsCoordinator'
  ) {
    return null
  }

  const SortableItem = SortableElement(({ data }) => {
    const { url, ...dataWithoutUrl } = data
    return (
      <Bookmark data={dataWithoutUrl}>
        <Flex
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          opacity={0}
          background="linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.92) 70%)"
          alignItems="center"
          justifyContent="flex-end"
          paddingRight="12px"
          css={css`
            cursor: move;
            .IconButton__hint {
              bottom: inherit;
              left: inherit;
              right: 14px;
              top: 7px;
              ::before {
                right: -5px;
                left: inherit;
                bottom: 40%;
                transform: rotate(-90deg);
              }
            }
            :hover {
              opacity: 1;
            }
          `}
        >
          <Flex
            width="68px"
            color={brandColors.grey.mid}
            justifyContent="space-between"
            paddingRight="8px"
            css={css`
              .IconButton.medium:hover:before {
                box-shadow: 0 0 0 13px rgba(177, 191, 197, 0.33);
              }
            `}
          >
            <ButtonIcon
              circular
              hint={t`Edit`}
              aria-label={t`Edit`}
              icon={<IconPencil />}
              color="medium"
              onClick={() => {
                setBookmarkEditId(data.id)
                setShowBookmarkFormModal(true)
              }}
            />
            <ButtonIcon
              circular
              hint="Delete"
              aria-label="Delete"
              icon={<IconTrash />}
              color="medium"
              onClick={() => {
                setRemoveModalBookmarkId(data.id)
              }}
            />
          </Flex>
        </Flex>
      </Bookmark>
    )
  })

  const SortableBookmarks = SortableContainer(({ bookmarks }) => {
    return (
      <Flex
        backgroundColor={brandColors.grey.lightest}
        borderRadius="4px"
        mt="8px"
        flexDirection="column"
        flexGrow={1}
      >
        <FlexBoxGrid
          columns={['300px', '250px', '30%', '30%']}
          flexWrap="wrap"
          columnGap="16px"
          rowGap="16px"
          padding="16px"
        >
          {bookmarks.map((data, index) => {
            return (
              <Box key={index}>
                <SortableItem index={index} data={data} />
              </Box>
            )
          })}
        </FlexBoxGrid>
        <Flex
          padding="4px 16px 16px"
          alignItems="center"
          justifyContent="center"
          color={brandColors.grey[300]}
        >
          <IconCursorMove />
          <Box marginLeft="8px">
            <Trans>Use drag & drop to change the order</Trans>
          </Box>
        </Flex>
      </Flex>
    )
  })

  return (
    <>
      <Box p="0 16px" className="Bookmarks">
        <Flex direction="row" alignContent="space-between">
          <Flex direction="column" flexGrow={1}>
            <h3>
              <Trans>Bookmarks</Trans>
            </h3>
            <FormRow>
              <Flex direction="row" alignItems="center">
                <Box mr="12px">
                  <label>
                    <Trans>Large bookmarks</Trans>
                  </label>
                </Box>
                <Switch
                  isChecked={largerBookmark}
                  onChange={async () => {
                    setLargerBookmark(!largerBookmark)
                    await setBookmarksType({
                      variables: {
                        workspaceId: workspaceId,
                        targetType: largerBookmark
                          ? WorkspaceBookmarkTypeEnum.NORMAL
                          : WorkspaceBookmarkTypeEnum.BIG
                      }
                    })
                    refetchBookmarks()
                  }}
                />
                <Box ml="12px" fontSize="12px" color={brandColors.grey[300]}>
                  <Trans>Will also add ability to set custom background.</Trans>
                </Box>
              </Flex>
            </FormRow>
          </Flex>
          <Box alignSelf="flex-start">
            <ButtonOutline
              onClick={() => {
                setBookmarkEditId(null)
                setShowBookmarkFormModal(true)
              }}
              leftIcon={<IconPlus2 />}
            >
              <Trans>Add bookmark</Trans>
            </ButtonOutline>
          </Box>
        </Flex>

        <Flex position="relative">
          <SortableBookmarks
            bookmarks={bookmarks}
            onSortEnd={async ({ oldIndex, newIndex }) => {
              await setBookmarksOrder({
                variables: {
                  workspaceId: workspaceId,
                  orderOfIds: arrayMove(
                    bookmarks || [],
                    oldIndex,
                    newIndex
                  ).map((bookmark: typeof bookmarks[0]) => bookmark.id)
                }
              })
              refetchBookmarks()
            }}
            axis="xy"
            distance={1}
          />
          {loading && (
            <Flex
              backgroundColor="rgba(255,255,255,.7)"
              left={0}
              top={0}
              right={0}
              bottom={0}
              position="absolute"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Flex>
          )}
        </Flex>
      </Box>

      {showBookmarkFormModal && (
        <BookmarkFormModal
          bookmarkToEdit={
            bookmarkEditId && bookmarks
              ? bookmarks.find((bookmark) => bookmark.id === bookmarkEditId) ||
                null
              : null
          }
          workspaceId={workspaceId}
          modalVisible={showBookmarkFormModal}
          hideModal={() => {
            setShowBookmarkFormModal(false)
            setBookmarkEditId(null)
          }}
          onBookmarkChange={async () => {
            refetchBookmarks()
          }}
          showImageUpload={largerBookmark}
        />
      )}
      <GeneralRemoveModal
        show={!!removeModalBookmarkId}
        actionName={t`Delete`}
        header={t`Delete bookmark`}
        entityName={t`bookmark`}
        confirmButton={t`Delete`}
        onDeleted={() => setRemoveModalBookmarkId(null)}
        onCanceled={() => setRemoveModalBookmarkId(null)}
        operation={async () => {
          await removeBookmark({
            variables: {
              workspaceId: workspaceId,
              bookmarkId: removeModalBookmarkId!
            }
          })
          refetchBookmarks()
        }}
      />
    </>
  )
}
